import React from 'react';
import { Story } from '../types';
import { shareStory } from '../services/storageService';

interface LibraryProps {
  stories: Story[];
  onSelect: (story: Story) => void;
  onDelete: (id: string) => void;
}

const Library: React.FC<LibraryProps> = ({ stories, onSelect, onDelete }) => {
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [sharingId, setSharingId] = React.useState<string | null>(null);
  const [shareSuccessId, setShareSuccessId] = React.useState<string | null>(null);

  const handleShare = async (e: React.MouseEvent, story: Story) => {
    e.stopPropagation();
    try {
      setSharingId(story.id);
      const storyId = await shareStory(story);
      const shareUrl = `${window.location.origin}${window.location.pathname}?story=${storyId}`;
      
      if (navigator.share) {
        await navigator.share({
          title: `Përralla: ${story.title}`,
          text: `Shiko këtë përrallë magjike të krijuar me PërrallAIme!`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareSuccessId(story.id);
        setTimeout(() => setShareSuccessId(null), 3000);
      }
    } catch (err) {
      console.error("Gabim gjatë shpërndarjes:", err);
    } finally {
      setSharingId(null);
    }
  };

  if (stories.length === 0) {
    return (
      <div className="text-center py-24 opacity-50 animate-in fade-in zoom-in duration-1000">
        <div className="flex justify-center mb-6">
          <span className="text-7xl">🌴</span>
        </div>
        <h2 className="text-3xl font-bold text-white font-fairytale tracking-wide text-glow-yellow">Libraria është bosh...</h2>
        <p className="text-indigo-100/70 text-sm mt-2">Krijo përrallën e parë magjike për ta mbushur atë!</p>
      </div>
    );
  }

  return (
    <section className="animate-in fade-in duration-700 w-full px-1">
      <div className="flex items-center gap-4 mb-8">
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-fairytale whitespace-nowrap text-glow-yellow">
          Përrallat e mia
        </h2>
        <div className="flex-grow h-[2px] bg-gradient-to-r from-white/20 to-transparent"></div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6 lg:gap-8">
        {stories.map((story, index) => {
          // Fallback for older stories and safety check
          const parts = story.parts || [];
          const thumbnail = parts[0]?.imageUrl || (parts[0] as any)?.image;
          
          return (
            <div 
              key={story.id || index}
              className="group glass-card rounded-[2rem] overflow-hidden hover:scale-[1.03] hover:shadow-2xl transition-all duration-300 flex flex-col border border-white/20 relative"
            >
              <div 
                className="aspect-[4/5] bg-white/10 cursor-pointer overflow-hidden relative"
                onClick={() => onSelect(story)}
              >
                {thumbnail ? (
                  <img 
                    src={thumbnail} 
                    alt={story.title} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-5xl opacity-20">📖</span>
                  </div>
                )}
                
                {/* Overlay on hover for better visibility */}
                <div className="absolute inset-0 bg-indigo-950/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeletingId(story.id);
                  }}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500/90 text-white flex items-center justify-center shadow-xl opacity-60 group-hover:opacity-100 transition-all hover:bg-red-600 active:scale-90 z-10"
                >
                  <span className="text-sm">🗑️</span>
                </button>

                <button 
                  onClick={(e) => handleShare(e, story)}
                  disabled={sharingId === story.id}
                  className={`absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center shadow-xl opacity-60 group-hover:opacity-100 transition-all active:scale-90 z-10 ${
                    shareSuccessId === story.id 
                      ? 'bg-green-500 text-white' 
                      : 'bg-purple-600/90 text-white hover:bg-purple-700'
                  }`}
                  title="Shpërndaj"
                >
                  <span className={`text-xs ${sharingId === story.id ? 'animate-spin' : ''}`}>
                    {shareSuccessId === story.id ? '✅' : sharingId === story.id ? '⏳' : '🔗'}
                  </span>
                </button>

                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                   <div className="text-yellow-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                      <span className="text-xs">✨</span> {(story.parts || []).length} Skena
                   </div>
                </div>
              </div>
              
              <div className="p-4 flex flex-col flex-grow bg-indigo-950/20">
                <h3 
                  className="text-sm md:text-base font-bold text-white font-fairytale line-clamp-2 cursor-pointer leading-tight flex-grow group-hover:text-yellow-400 transition-colors"
                  onClick={() => onSelect(story)}
                >
                  {story.title}
                </h3>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                  <p className="text-[9px] text-white/60 font-black uppercase tracking-tighter">
                    {new Date(story.createdAt).toLocaleDateString('sq-AL', { day: 'numeric', month: 'short' })}
                  </p>
                  <span className="text-indigo-300 text-[10px] group-hover:translate-x-1 transition-transform">Lexo →</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom Confirmation Modal */}
      {deletingId && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="glass-card p-8 rounded-[2.5rem] border border-white/20 shadow-2xl max-w-sm w-full text-center space-y-6 bg-indigo-950/90">
            <div className="text-5xl">⚠️</div>
            <h3 className="text-2xl font-bold text-white font-fairytale">Fshij Përrallën?</h3>
            <p className="text-indigo-100/70 text-sm">
              A jeni të sigurt që dëshironi ta fshini këtë përrallë përgjithmonë? Ky veprim nuk mund të kthehet.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setDeletingId(null)}
                className="flex-1 py-3 rounded-2xl bg-white/10 text-white font-bold hover:bg-white/20 transition-all"
              >
                Anulo
              </button>
              <button 
                onClick={() => {
                  onDelete(deletingId);
                  setDeletingId(null);
                }}
                className="flex-1 py-3 rounded-2xl bg-red-500 text-white font-bold hover:bg-red-600 shadow-lg shadow-red-500/20 transition-all"
              >
                Fshij
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Library;